import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Gift, Copy, Check, Flame, Heart, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";
import { toast } from "sonner";

export default function GiftCreate() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const preselected = sp.get("companion") || "";
  const { user, refresh } = useAuth();
  const [companions, setCompanions] = useState([]);
  const [companionId, setCompanionId] = useState(preselected);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [gift, setGift] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/companions").then((r) => setCompanions(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Gift className="h-10 w-10 text-rose-400 mx-auto mb-4" />
        <h1 className="font-serif text-3xl font-bold mb-3">Sign in to send a gift</h1>
        <p className="text-slate-400 mb-6">Gift a Steamy 24-hour window with any companion. 10 gems.</p>
        <Button onClick={login} className="rounded-full bg-amber-500 text-black font-semibold px-7 h-12">Sign in</Button>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (!companionId) { toast.error("Pick a companion"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gifts", { companion_id: companionId, kind: "steamy_24h", note });
      setGift(data);
      await refresh();
      toast.success("Gift created — share the link below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create gift");
    } finally { setBusy(false); }
  };

  const giftUrl = gift ? `${window.location.origin}/gift/${gift.gift_id}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(giftUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mb-8">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>
      <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-400 mb-2 flex items-center gap-2">
        <Gift className="h-3 w-3" /> Gift a Session
      </div>
      <h1 className="font-serif text-3xl md:text-4xl font-bold tracking-tight">Send a Steamy 24-hour window.</h1>
      <p className="mt-3 text-slate-400">Buy a one-time 24h Steamy Mode window with any companion. Share the link with a friend — they claim it and the clock starts.</p>

      {!gift ? (
        <form onSubmit={create} className="mt-8 space-y-5 rounded-3xl border border-white/10 bg-[#0F1117] p-6 md:p-8">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Companion</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
              {companions.map((c) => {
                const on = companionId === c.companion_id;
                return (
                  <button
                    type="button"
                    key={c.companion_id}
                    data-testid={`gift-pick-${c.companion_id}`}
                    onClick={() => setCompanionId(c.companion_id)}
                    className={`text-left rounded-xl border p-2 transition ${on ? "border-rose-500/70 bg-rose-500/10" : "border-white/10 bg-[#07080A] hover:border-white/20"}`}
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden mb-1">
                      <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="text-[10px] text-slate-300 font-serif truncate">{c.name}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Note (optional)</Label>
            <Textarea data-testid="gift-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={280} rows={2} placeholder="For my favorite over-worker, with love." className="mt-1 bg-[#07080A] border-white/10" />
          </div>
          <Button data-testid="gift-submit" type="submit" disabled={busy} className="w-full rounded-full bg-rose-500 hover:bg-rose-400 text-white font-semibold h-11">
            {busy ? "Creating…" : "Create gift · 10 gems"}
          </Button>
        </form>
      ) : (
        <div className="mt-8 rounded-3xl border border-amber-500/40 bg-gradient-to-br from-amber-500/8 to-rose-500/8 p-6 md:p-8">
          <div className="inline-flex items-center gap-2 text-xs text-amber-300 font-mono uppercase tracking-widest mb-3">
            <Flame className="h-3 w-3" /> Ready to send
          </div>
          <h2 className="font-serif text-2xl font-bold mb-2">A Steamy 24h with {gift.companion_name}</h2>
          <p className="text-slate-400 text-sm mb-4">Share this link. Whoever opens it first claims the gift.</p>
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#07080A] p-3">
            <span data-testid="gift-url" className="flex-1 font-mono text-xs text-amber-300 truncate">{giftUrl}</span>
            <Button data-testid="gift-copy" size="sm" onClick={copy} variant="outline" className="rounded-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-8 px-3">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button onClick={() => { setGift(null); setNote(""); }} variant="outline" className="rounded-full border-white/15">Send another</Button>
            <Button onClick={() => nav("/dashboard")} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold">
              <Heart className="h-4 w-4 mr-2" /> Back to dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
